import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef, useCallback } from 'react';

export interface DrawData {
    x0: number; y0: number; x1: number; y1: number;
    color: string; lineWidth: number;
}

interface CanvasProps {
    color: string;
    lineWidth: number;
    disabled: boolean;
}

export const Canvas = forwardRef<any, CanvasProps>(({ color, lineWidth, disabled }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const lastPosition = useRef<{ x: number; y: number } | null>(null);
    const [drawingData, setDrawingData] = useState<DrawData[]>([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                setContext(ctx);
            }
        }
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !context) return;
        let animationFrameId: number;
        const resizeObserver = new ResizeObserver(entries => {
            animationFrameId = window.requestAnimationFrame(() => {
                if (!entries || entries.length === 0) return;
                const { width } = entries[0].contentRect;
                const currentDrawing = [...drawingData];
                canvas.width = width;
                canvas.height = width;
                context.lineCap = 'round';
                context.lineJoin = 'round';
                currentDrawing.forEach(data => {
                    context.globalCompositeOperation = data.color === 'eraser' ? 'destination-out' : 'source-over';
                    context.beginPath();
                    context.strokeStyle = data.color === 'eraser' ? '#000' : data.color;
                    context.lineWidth = data.lineWidth;
                    context.moveTo(data.x0 * width, data.y0 * width);
                    context.lineTo(data.x1 * width, data.y1 * width);
                    context.stroke();
                    context.closePath();
                });
                context.globalCompositeOperation = 'source-over';
            });
        });
        if (canvas.parentElement) resizeObserver.observe(canvas.parentElement);
        return () => {
            if (canvas.parentElement) resizeObserver.unobserve(canvas.parentElement);
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [context, drawingData]);

    useImperativeHandle(ref, () => ({
        clearCanvas: () => {
            if (context && canvasRef.current) {
                context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                setDrawingData([]);
            }
        },
        getDrawingData: () => drawingData,
    }));

    const unifiedDraw = useCallback((x1: number, y1: number) => {
        if (!isDrawing || !context || !lastPosition.current) return;
        context.globalCompositeOperation = color === 'eraser' ? 'destination-out' : 'source-over';
        context.beginPath();
        context.strokeStyle = color === 'eraser' ? '#000' : color;
        context.lineWidth = lineWidth;
        context.moveTo(lastPosition.current.x, lastPosition.current.y);
        context.lineTo(x1, y1);
        context.stroke();
        context.closePath();
        const newDataPoint: DrawData = {
            x0: lastPosition.current.x / canvasRef.current!.width,
            y0: lastPosition.current.y / canvasRef.current!.height,
            x1: x1 / canvasRef.current!.width,
            y1: y1 / canvasRef.current!.height,
            color,
            lineWidth,
        };
        setDrawingData(prevData => [...prevData, newDataPoint]);
        lastPosition.current = { x: x1, y: y1 };
    }, [isDrawing, context, color, lineWidth]);

    const unifiedStart = useCallback((x: number, y: number) => {
        if (disabled) return;
        setIsDrawing(true);
        lastPosition.current = { x, y };
    }, [disabled]);

    const unifiedStop = useCallback(() => {
        setIsDrawing(false);
        lastPosition.current = null;
    }, []);

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => unifiedStart(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => unifiedDraw(e.nativeEvent.offsetX, e.nativeEvent.offsetY);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handleTouchStart = (e: TouchEvent) => {
            e.preventDefault();
            const { clientX, clientY } = e.touches[0];
            const { left, top } = canvas.getBoundingClientRect();
            unifiedStart(clientX - left, clientY - top);
        };

        const handleTouchMove = (e: TouchEvent) => {
            e.preventDefault();
            const { clientX, clientY } = e.touches[0];
            const { left, top } = canvas.getBoundingClientRect();
            unifiedDraw(clientX - left, clientY - top);
        };

        canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
        canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
        canvas.addEventListener('touchend', unifiedStop, { passive: false });
        canvas.addEventListener('touchcancel', unifiedStop, { passive: false });

        return () => {
            canvas.removeEventListener('touchstart', handleTouchStart);
            canvas.removeEventListener('touchmove', handleTouchMove);
            canvas.removeEventListener('touchend', unifiedStop);
            canvas.removeEventListener('touchcancel', unifiedStop);
        };
    }, [unifiedStart, unifiedDraw, unifiedStop]);

    return (
        <canvas
            ref={canvasRef}
            style={{ backgroundColor: '#4a4a4a', border: '4px solid #000', touchAction: 'none', width: '100%' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={unifiedStop}
            onMouseLeave={unifiedStop}
        />
    );
});